import { read, utils } from 'xlsx';
import { normalizeStockHeader } from './customerStockFields';
import { StockColumnChoice, suggestStockColumns } from './suggestStockColumns';

const cellValue = (value: unknown) => String(value ?? '').trim();

export type ParsedStockFile = {
  headers: string[];
  rows: Record<string, string>[];
  columns: StockColumnChoice;
};

export const readStockSheet = (
  headers: string[],
  rows: Record<string, unknown>[],
): ParsedStockFile => {
  const uniqueHeaders: string[] = [];
  const seen = new Set<string>();
  headers.forEach((header) => {
    const name = String(header ?? '').trim();
    const key = normalizeStockHeader(name);
    if (!name || seen.has(key)) return;
    seen.add(key);
    uniqueHeaders.push(name);
  });

  const cleanedRows = rows
    .map((record) => {
      const row: Record<string, string> = {};
      uniqueHeaders.forEach((header) => {
        const key = Object.keys(record).find(
          (name) => normalizeStockHeader(name) === normalizeStockHeader(header),
        );
        row[header] = cellValue(key ? record[key] : '');
      });
      return row;
    })
    .filter((row) => uniqueHeaders.some((header) => row[header]));

  return {
    headers: uniqueHeaders,
    rows: cleanedRows,
    columns: suggestStockColumns(uniqueHeaders, cleanedRows),
  };
};

export const parseStockFile = async (file: File): Promise<ParsedStockFile> => {
  const workbook = read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return readStockSheet([], []);
  const records = utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: '',
    raw: false,
  });
  return readStockSheet(records.length ? Object.keys(records[0]) : [], records);
};
